import * as pdfjsLib from "pdfjs-dist";
import { getReadSignedUrl } from "frontend/api";

interface CachedPDF {
  fileId: string;
  pdfDocument: pdfjsLib.PDFDocumentProxy;
  signedUrl: string;
  signedUrlExpiry: number;
  lastAccessed: number;
  refCount: number;
}

class PDFCacheService {
  private cache: Map<string, CachedPDF> = new Map();
  private loadingPromises: Map<string, Promise<CachedPDF>> = new Map();

  // Cache configuration
  private readonly SIGNED_URL_VALIDITY = 3600 * 1000; // 1 hour
  private readonly MAX_CACHE_SIZE = 50; // Maximum PDFs in cache
  private readonly CLEANUP_INTERVAL = 300 * 1000; // 5 minutes

  constructor() {
    // Start periodic cleanup
    setInterval(() => this.cleanup(), this.CLEANUP_INTERVAL);
  }

  async getPDF(fileId: string, fileUrl?: string): Promise<pdfjsLib.PDFDocumentProxy> {
    const cacheKey = fileId.toString();

    // Check if already loading
    if (this.loadingPromises.has(cacheKey)) {
      const cached = await this.loadingPromises.get(cacheKey)!;
      return cached.pdfDocument;
    }

    // Check cache
    const cached = this.cache.get(cacheKey);

    if (cached && this.isSignedUrlValid(cached)) {
      cached.lastAccessed = Date.now();
      cached.refCount++;
      return cached.pdfDocument;
    }

    if (!fileUrl) {
      throw new Error("File URL is required");
    }

    // Load PDF
    const loadPromise = this.loadPDF(fileId, fileUrl);
    this.loadingPromises.set(cacheKey, loadPromise);

    try {
      const result = await loadPromise;
      this.cache.set(cacheKey, result);
      return result.pdfDocument;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  private async loadPDF(fileId: string, fileUrl: string): Promise<CachedPDF> {
    // Get signed URL
    const signedUrlResponse = await getReadSignedUrl(fileUrl);
    const signedUrl = signedUrlResponse.url;

    // Load PDF document
    const loadingTask = pdfjsLib.getDocument(signedUrl);
    const pdfDocument = await loadingTask.promise;

    return {
      fileId: fileId.toString(),
      pdfDocument,
      signedUrl,
      signedUrlExpiry: Date.now() + this.SIGNED_URL_VALIDITY,
      lastAccessed: Date.now(),
      refCount: 1,
    };
  }

  private isSignedUrlValid(cached: CachedPDF): boolean {
    return Date.now() < cached.signedUrlExpiry;
  }

  release(fileId: string): void {
    const cacheKey = fileId.toString();
    const cached = this.cache.get(cacheKey);
    if (cached) {
      cached.refCount--;
      if (cached.refCount <= 0) {
        // Mark for potential cleanup
        cached.refCount = 0;
      }
    }
  }

  private cleanup(): void {
    // Remove expired or unused PDFs
    const now = Date.now();
    const entriesToRemove: string[] = [];

    for (const [key, cached] of this.cache.entries()) {
      // Remove if expired or not accessed recently
      if (
        !this.isSignedUrlValid(cached) ||
        (cached.refCount === 0 && now - cached.lastAccessed > this.CLEANUP_INTERVAL)
      ) {
        entriesToRemove.push(key);
      }
    }

    // Remove entries
    entriesToRemove.forEach((key) => {
      const cached = this.cache.get(key);
      if (cached?.pdfDocument) {
        // Cleanup PDF.js resources
        cached.pdfDocument.destroy();
      }
      this.cache.delete(key);
    });

    // Enforce cache size limit (LRU)
    if (this.cache.size > this.MAX_CACHE_SIZE) {
      const sortedEntries = Array.from(this.cache.entries()).sort((a, b) => a[1].lastAccessed - b[1].lastAccessed);

      const toRemove = sortedEntries.slice(0, this.cache.size - this.MAX_CACHE_SIZE);
      toRemove.forEach(([key, cached]) => {
        if (cached.refCount === 0) {
          cached.pdfDocument.destroy();
          this.cache.delete(key);
        }
      });
    }
  }

  clearAll(): void {
    this.cache.forEach((cached) => {
      cached.pdfDocument.destroy();
    });
    this.cache.clear();
    this.loadingPromises.clear();
  }

  // Debug methods
  getCacheStats(): { size: number; loadingCount: number } {
    return {
      size: this.cache.size,
      loadingCount: this.loadingPromises.size,
    };
  }
}

// Singleton instance
export const pdfCacheService = new PDFCacheService();
